import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { apiService } from '../services/apiService';
import { DSAlert, DSBadge, DSButton, DSCard, DSIcon, DSInput, DSSelect, DSTextarea } from '../design-system/primitives';
import { showToast } from './Toast';
import StatusBadge from './StatusBadge';
import TournamentDetailsFields from './TournamentDetailsFields';
import './PinballEventForm.css';
import { personName } from '../utils/personName';

const menuPortalStyles = {
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999
  })
};

/**
 * The form's state as the events API takes it. The rich HTML description lives in
 * event_metadata, where the website sync reads it as the page body; at the top level
 * the server never stored it, so it was silently dropped.
 */
function toEventPayload(formData) {
  const { html_description: htmlDescription, ...rest } = formData;
  return {
    ...rest,
    event_metadata: {
      ...rest.event_metadata,
      ...(htmlDescription && htmlDescription.trim() ? { html_description: htmlDescription } : {})
    }
  };
}

function PinballEventForm({ onClose, onSuccess, initialPinballSubtype = 'tournament' }) {
  const [categories, setCategories] = useState([]);
  const [hostCandidates, setHostCandidates] = useState([]);
  const [machineCandidates, setMachineCandidates] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedHostIds, setSelectedHostIds] = useState([]);
  const [selectedMachineId, setSelectedMachineId] = useState('');
  const [pinballSubtype, setPinballSubtype] = useState(initialPinballSubtype);
  const [pingolfConfig, setPingolfConfig] = useState({
    cardPriceDollars: '1',
    payoutPreset: 'winner_take_all',
    machines: []
  });
  const [formData, setFormData] = useState({
    title: '',
    description: 'Join us for another exciting Capital Cup tournament! This IFPA-sanctioned event features competitive pinball action.',
    html_description: '',
    event_date: '',
    location: 'Capital Pinball Club, 70 Pembroke Road, Concord, NH',
    event_category: 'pinball',
    duration_minutes: '240',
    // An existing website event to update instead of creating a new page (address or ID).
    wordpress_link: '',
    event_metadata: {
      matchplay_url: '',
      featured_image_url: '',
      checkin_time: '12:00 PM',
      tournament_fee: '15',
      day_pass_fee: '15',
      qualifier_format: '',
      finals_format: '',
      num_rounds: '',
      finals_structure: 'Top 8 make finals',
      prize_info: 'Trophies and prizes for the top 4',
      // The all-in price the website shows, when it is more than the tournament fee.
      cost: '',
      // Website categories for a one-off event, which has no series to carry them.
      wordpress_categories: 'capital-cup, ifpa, tournament'
    }
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [createdEventId, setCreatedEventId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const isPingolfMode = pinballSubtype === 'pingolf';

  useEffect(() => {
    fetchCategories();
    fetchHostCandidates();
    fetchMachineCandidates();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await apiService.get('/event-categories');
      setCategories(response.data);
      
      // Pre-select default categories for pinball
      const defaultCategories = response.data
        .filter(cat => cat.is_default_for_pinball)
        .map(cat => cat.id);
      setSelectedCategories(defaultCategories);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchHostCandidates = async () => {
    try {
      const response = await apiService.get('/users?hostEligible=true');
      setHostCandidates(response.data || []);
    } catch (err) {
      console.error('Error fetching host candidates:', err);
    }
  };

  const fetchMachineCandidates = async () => {
    try {
      const response = await apiService.get('/machines');
      const activeMachines = (response.data || []).filter(machine => machine.is_active !== false);
      setMachineCandidates(activeMachines);
    } catch (err) {
      console.error('Error fetching machine candidates:', err);
      setError('Failed to load machines for PinGolf setup.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMetadataChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      event_metadata: {
        ...prev.event_metadata,
        [name]: value
      }
    }));
  };

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  const handleAddPingolfMachine = () => {
    const machineId = parseInt(selectedMachineId, 10);
    if (!machineId) {
      return;
    }

    const selectedMachine = machineCandidates.find(machine => machine.id === machineId);
    if (!selectedMachine) {
      return;
    }

    setPingolfConfig(prev => {
      if (prev.machines.some(machine => machine.machineId === machineId)) {
        return prev;
      }

      return {
        ...prev,
        machines: [
          ...prev.machines,
          {
            machineId,
            machineName: selectedMachine.name,
            targetScore: selectedMachine.pingolf_target_score || '',
            maxBalls: selectedMachine.pingolf_ball_count || 3
          }
        ]
      };
    });

    setSelectedMachineId('');
  };

  const handleRemovePingolfMachine = (machineId) => {
    setPingolfConfig(prev => ({
      ...prev,
      machines: prev.machines.filter(machine => machine.machineId !== machineId)
    }));
  };

  const handlePingolfMachineFieldChange = (machineId, field, value) => {
    setPingolfConfig(prev => ({
      ...prev,
      machines: prev.machines.map(machine => {
        if (machine.machineId !== machineId) {
          return machine;
        }

        return {
          ...machine,
          [field]: value
        };
      })
    }));
  };

  const handlePingolfConfigChange = (e) => {
    const { name, value } = e.target;
    setPingolfConfig(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      // Convert the local datetime to UTC for storage
      const localDate = new Date(formData.event_date);
      const timezoneOffset = localDate.getTimezoneOffset() * 60000;
      const utcDate = new Date(localDate.getTime() - timezoneOffset);

      if (pinballSubtype === 'pingolf') {
        const parsedCardPrice = Number(pingolfConfig.cardPriceDollars);
        if (selectedHostIds.length === 0) {
          setError('PinGolf events require at least one assigned host.');
          return;
        }

        if (pingolfConfig.machines.length === 0) {
          setError('PinGolf events require at least one selected machine.');
          return;
        }

        if (!Number.isFinite(parsedCardPrice) || parsedCardPrice <= 0) {
          setError('PinGolf card price must be greater than 0.');
          return;
        }

        const invalidMachine = pingolfConfig.machines.find((machine) => {
          const targetScore = Number(machine.targetScore);
          const maxBalls = Number(machine.maxBalls);
          return !Number.isFinite(targetScore) || targetScore <= 0 || !Number.isFinite(maxBalls) || maxBalls <= 0;
        });

        if (invalidMachine) {
          setError(`PinGolf machine settings are incomplete. Set valid target score and max balls for ${invalidMachine.machineName}.`);
          return;
        }

        const pingolfMachines = pingolfConfig.machines.map(machine => {
          const targetScore = Number(machine.targetScore);
          const maxBalls = Number(machine.maxBalls);

          return {
            machineId: machine.machineId,
            targetScore,
            maxBalls
          };
        });

        const eventData = {
          title: formData.title,
          description: formData.description,
          event_date: utcDate.toISOString(),
          location: formData.location,
          event_category: 'pinball',
          duration_minutes: parseInt(formData.duration_minutes, 10) || 240,
          host_ids: selectedHostIds.map(id => parseInt(id, 10)),
          event_metadata: {
            pinball_format: 'pingolf',
            pingolf: {
              cardPriceDollars: parsedCardPrice,
              payoutPreset: pingolfConfig.payoutPreset,
              machines: pingolfMachines
            }
          }
        };

        const eventResponse = await apiService.post('/events', eventData);
        const eventId = eventResponse.data.id;

        for (const categoryId of selectedCategories) {
          try {
            await apiService.post(`/event-categories/event/${eventId}/assign`, { categoryId });
          } catch (err) {
            console.error(`Failed to assign category ${categoryId}:`, err);
          }
        }

        if (onSuccess) {
          onSuccess();
        }

        onClose();
        return;
      }

      const eventData = {
        ...toEventPayload(formData),
        event_date: utcDate.toISOString()
      };

      const eventResponse = await apiService.post('/events', eventData);
      const eventId = eventResponse.data.id;
      setCreatedEventId(eventId);

      for (const categoryId of selectedCategories) {
        try {
          await apiService.post(`/event-categories/event/${eventId}/assign`, { categoryId });
        } catch (err) {
          console.error(`Failed to assign category ${categoryId}:`, err);
        }
      }

      showToast(formData.wordpress_link.trim()
        ? 'Event created and linked to the existing website page. Click "Sync to WordPress" to update that page.'
        : 'Event created successfully! Click "Sync to WordPress" to get the WordPress URL for MatchPlay.', 'success');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create event');
      console.error('Error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSyncToWordPress = async () => {
    if (!createdEventId) return;
    
    setSyncing(true);
    setError(null);
    
    try {
      const syncResponse = await apiService.post(`/automation/sync-wordpress/${createdEventId}`);
      
      if (syncResponse.data.wordpressUrl) {
        // Update form data with WordPress URL
        setFormData(prev => ({
          ...prev,
          wordpress_url: syncResponse.data.wordpressUrl,
          event_metadata: {
            ...prev.event_metadata,
            event_url: syncResponse.data.wordpressUrl
          }
        }));
        
        showToast(`WordPress sync successful!

WordPress URL: ${syncResponse.data.wordpressUrl}

This URL is now in the form. Use it when creating your MatchPlay tournament, then come back and add the MatchPlay URL.`, 'success');
      }
    } catch (err) {
      setError('WordPress sync failed. You can try again or sync manually from Automation Settings.');
      console.error('WordPress sync error:', err);
    } finally {
      setSyncing(false);
    }
  };

  const handleUpdateEvent = async () => {
    if (!createdEventId) return;
    
    setSubmitting(true);
    setError(null);
    
    try {
      // Update the event with new data (like MatchPlay URL)
      // The link was set when the event was created; sending it again would re-check it
      // against the event itself.
      const { wordpress_link: _link, ...payload } = toEventPayload(formData);
      await apiService.put(`/events/${createdEventId}`, payload);
      
      // Re-sync to WordPress with updated data
      await apiService.post(`/automation/sync-wordpress/${createdEventId}`);
      
      showToast('Event updated and re-synced to WordPress successfully!', 'success');
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create event');
      console.error('Error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      showToast('Copied to clipboard!', 'success');
    }).catch(err => {
      console.error('Failed to copy:', err);
      showToast('Failed to copy to clipboard', 'error');
    });
  };

  const generateIFPADetails = () => {
    const { event_metadata } = formData;
    const details = [];
    
    // Title
    details.push(formData.title);
    details.push('');
    
    // Description
    if (formData.description) {
      details.push(formData.description);
      details.push('');
    }
    
    // Times and Fees
    if (formData.event_date) {
      // Check-in time is the event date
      const checkInDate = new Date(formData.event_date);
      const checkInHours = checkInDate.getHours();
      const checkInMinutes = checkInDate.getMinutes();
      const checkInPeriod = checkInHours >= 12 ? 'PM' : 'AM';
      const checkInDisplayHours = checkInHours % 12 || 12;
      const checkInDisplayMinutes = checkInMinutes > 0 ? `:${checkInMinutes.toString().padStart(2, '0')}` : '';
      details.push(`Check In: ${checkInDisplayHours}${checkInDisplayMinutes}${checkInPeriod}`);
      
      // Start time is 1 hour after check-in
      const startDate = new Date(formData.event_date);
      startDate.setHours(startDate.getHours() + 1);
      const startHours = startDate.getHours();
      const startMinutes = startDate.getMinutes();
      const startPeriod = startHours >= 12 ? 'PM' : 'AM';
      const startDisplayHours = startHours % 12 || 12;
      const startDisplayMinutes = startMinutes > 0 ? `:${startMinutes.toString().padStart(2, '0')}` : '';
      details.push(`Start Time: ${startDisplayHours}${startDisplayMinutes}${startPeriod}`);
    }
    if (event_metadata.tournament_fee) details.push(`Tournament Fee: $${event_metadata.tournament_fee}`);
    if (event_metadata.day_pass_fee) details.push(`Day Pass Fee (non-members): $${event_metadata.day_pass_fee}`);
    details.push('');
    
    // Qualifier Info
    if (event_metadata.qualifier_format) {
      // Only include "X Rounds of" if num_rounds is specified
      if (event_metadata.num_rounds) {
        details.push(`Qualifiers: ${event_metadata.num_rounds} Rounds of ${event_metadata.qualifier_format}`);
      } else {
        details.push(`Qualifiers: ${event_metadata.qualifier_format}`);
      }
      details.push('Scoring: IFPA scoring (7/5/3/1)');
    }
    
    // Finals Info
    if (event_metadata.finals_format) {
      details.push(`Finals: ${event_metadata.finals_format}`);
      if (event_metadata.finals_structure) details.push(event_metadata.finals_structure);
    }
    if (event_metadata.prize_info) details.push(event_metadata.prize_info);
    details.push('');
    
    // URLs - use wordpress_url if available, otherwise event_url from metadata
    const wordpressUrl = formData.wordpress_url || event_metadata.event_url;
    if (wordpressUrl) details.push(`Event Details: ${wordpressUrl}`);
    if (event_metadata.matchplay_url) details.push(`Registration: ${event_metadata.matchplay_url}`);
    
    return details.join('\n');
  };

  return (
    <div className={`pinball-event-form ${isPingolfMode ? 'pinball-event-form--pingolf' : ''}`}>
      <div className="form-header">
        <h2>{isPingolfMode ? 'Create PinGolf Event' : 'Create Pinball Tournament Event'}</h2>
        {isPingolfMode ? (
          <DSButton
            appearance="text"
            tone="neutral"
            size="sm"
            iconOnly
            aria-label="Close create event form"
            leadingIcon={<DSIcon name="x" size="sm" tone="default" />}
            onClick={onClose}
          />
        ) : (
          <button className="btn-close" onClick={onClose}>×</button>
        )}
      </div>

      {error && (isPingolfMode ? (
        <DSAlert variant="error" appearance="outline">{error}</DSAlert>
      ) : (
        <div className="error-message">{error}</div>
      ))}

      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <div className="form-section">
          <h3>Basic Information</h3>

          <div className="form-group">
            <DSSelect
              label="Pinball Event Type *"
              value={pinballSubtype}
              onChange={(e) => setPinballSubtype(e.target.value)}
              disabled={createdEventId !== null}
            >
              <option value="tournament">Tournament (MatchPlay/IFPA)</option>
              <option value="pingolf">PinGolf</option>
            </DSSelect>
          </div>

          <div className="form-group">
            <DSInput
              label="Event Title *"
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              placeholder="e.g., Capital Cup Monthly Championship"
            />
          </div>

          <div className="form-group">
            <DSInput
              label="Date & Time *"
              type="datetime-local"
              name="event_date"
              value={formData.event_date}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <DSInput
                label="Location"
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <DSInput
                label="Duration (minutes)"
                type="number"
                name="duration_minutes"
                min="1"
                value={formData.duration_minutes}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-group">
            <DSTextarea
              label="Basic Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="3"
              placeholder="Short description of the event..."
            />
          </div>
        </div>

        {pinballSubtype === 'pingolf' && (
          <>
            <DSCard
              title="PinGolf Setup"
              subtitle="Set the card price and payout shape before you publish the event."
              variant="outlined"
              bgVariant="brand"
              borderVariant="accent"
              cornerVariant="lg"
            >
              <div className="form-row">
                <div className="form-group">
                  <DSInput
                    label="Card Price ($)"
                    type="number"
                    min="0.01"
                    step="0.01"
                    name="cardPriceDollars"
                    value={pingolfConfig.cardPriceDollars}
                    onChange={handlePingolfConfigChange}
                  />
                </div>

                <div className="form-group">
                  <DSSelect
                    label="Payout Preset"
                    name="payoutPreset"
                    value={pingolfConfig.payoutPreset}
                    onChange={handlePingolfConfigChange}
                  >
                    <option value="winner_take_all">Winner Take All (100%)</option>
                    <option value="top_two_70_30">Top 2 (70/30)</option>
                    <option value="top_three_50_30_20">Top 3 (50/30/20)</option>
                  </DSSelect>
                </div>
              </div>
            </DSCard>

            <DSCard
              title="Assigned Hosts"
              subtitle="Pick at least one host who can operate sales, submissions, and scoring."
              variant="outlined"
              bgVariant="default"
              borderVariant="subtle"
              cornerVariant="lg"
            >
              <div className="host-select-container">
                <div className="pingolf-form-card-header">
                  <DSBadge variant="info" appearance="outline">{selectedHostIds.length} selected</DSBadge>
                </div>
                <Select
                  classNamePrefix="host-select"
                  isMulti
                  isSearchable
                  closeMenuOnSelect={false}
                  placeholder="Search and select hosts..."
                  options={hostCandidates.map((user) => {
                    const displayName = personName(user)
                      || user.username
                      || user.email;

                    return {
                      value: user.id,
                      label: `${displayName}${user.email ? ` (${user.email})` : ''}`
                    };
                  })}
                  value={hostCandidates
                    .filter(user => selectedHostIds.includes(user.id))
                    .map((user) => {
                      const displayName = personName(user)
                        || user.username
                        || user.email;

                      return {
                        value: user.id,
                        label: `${displayName}${user.email ? ` (${user.email})` : ''}`
                      };
                    })}
                  onChange={(options) => {
                    setSelectedHostIds((options || []).map(option => option.value));
                  }}
                  menuPortalTarget={document.body}
                  styles={menuPortalStyles}
                  noOptionsMessage={() => 'No hosts found'}
                />
                <small className="host-select-help">
                  Selected hosts: {selectedHostIds.length}
                </small>
              </div>
            </DSCard>

            <DSCard
              title="PinGolf Machine Lineup"
              subtitle="Select the course lineup and set each hole's target score and max balls."
              variant="outlined"
              bgVariant="default"
              borderVariant="subtle"
              cornerVariant="lg"
            >
              <div className="pingolf-form-card-header">
                <DSBadge variant="info" appearance="outline">{pingolfConfig.machines.length} holes</DSBadge>
              </div>
              <div className="machine-picker-row">
                <Select
                  classNamePrefix="machine-select"
                  isSearchable
                  placeholder="Search and select a machine..."
                  options={machineCandidates.map((machine) => ({
                    value: machine.id,
                    label: machine.name
                  }))}
                  value={machineCandidates
                    .filter(machine => String(machine.id) === String(selectedMachineId))
                    .map(machine => ({ value: machine.id, label: machine.name }))[0] || null}
                  onChange={(option) => setSelectedMachineId(option ? String(option.value) : '')}
                  menuPortalTarget={document.body}
                  styles={menuPortalStyles}
                  noOptionsMessage={() => 'No machines found'}
                />
                <DSButton
                  appearance="outline"
                  tone="secondary"
                  onClick={handleAddPingolfMachine}
                  disabled={!selectedMachineId}
                  leadingIcon={<DSIcon name="plus" size="sm" tone="brand" />}
                >
                  Add Machine
                </DSButton>
              </div>

              {pingolfConfig.machines.length === 0 ? (
                <p className="pingolf-empty-note">No machines selected yet.</p>
              ) : (
                <div className="machine-lineup-grid">
                  {pingolfConfig.machines.map(machine => (
                    <DSCard key={machine.machineId} variant="outlined" bgVariant="default" borderVariant="subtle" cornerVariant="md" className="machine-lineup-card">
                      <div className="machine-lineup-header">
                        <strong>{machine.machineName}</strong>
                        <DSButton
                          appearance="text"
                          tone="danger"
                          size="sm"
                          onClick={() => handleRemovePingolfMachine(machine.machineId)}
                          leadingIcon={<DSIcon name="x" size="sm" tone="danger" />}
                        >
                          Remove
                        </DSButton>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <DSInput
                            label="Target Score"
                            type="number"
                            min="1"
                            value={machine.targetScore}
                            onChange={(e) => handlePingolfMachineFieldChange(machine.machineId, 'targetScore', e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <DSInput
                            label="Max Balls"
                            type="number"
                            min="1"
                            value={machine.maxBalls}
                            onChange={(e) => handlePingolfMachineFieldChange(machine.machineId, 'maxBalls', e.target.value)}
                          />
                        </div>
                      </div>
                    </DSCard>
                  ))}
                </div>
              )}
            </DSCard>
          </>
        )}

        {/* MatchPlay Integration */}
        {/* Tournament Details, shared with the Edit Event form */}
        {pinballSubtype === 'tournament' && (
        <div className="form-section">
          <h3>Tournament Details</h3>
          <small>
            <strong>MatchPlay workflow:</strong> Create this event first, then use the Copy/Paste Helpers below to create the MatchPlay tournament.
            Come back and edit this event to add the MatchPlay URL once created.
          </small>

          <TournamentDetailsFields metadata={formData.event_metadata} onChange={handleMetadataChange} />

          <div className="form-group">
            <DSInput
              label="Existing website event (optional)"
              type="text"
              name="wordpress_link"
              value={formData.wordpress_link}
              onChange={handleInputChange}
              placeholder="https://capitalpinballclub.com/event/your-event/"
              disabled={Boolean(createdEventId)}
            />
            <small>
              Already made this event's page on the website? Paste its address here so syncing
              updates that page instead of creating a second one. The sync replaces the page with
              what is in this form, so paste the page's text into Rich HTML Description under Advanced Options.
            </small>
          </div>
        </div>
        )}

        {/* Categories */}
        <div className="form-section">
          <h3>Event Categories</h3>
          <div className="categories-checklist">
            {categories.map(category => (
              <label key={category.id} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={selectedCategories.includes(category.id)}
                  onChange={() => handleCategoryToggle(category.id)}
                />
                <span>{category.name}</span>
                {category.is_default_for_pinball && <StatusBadge variant="info" size="sm">Default</StatusBadge>}
              </label>
            ))}
          </div>
        </div>

        {/* Advanced Options */}
        {pinballSubtype === 'tournament' && (
        <div className="form-section">
          <button 
            type="button" 
            className="btn-toggle-advanced"
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            {showAdvanced ? <DSIcon name="chevronDown" /> : <DSIcon name="chevronRight" />} Advanced Options
          </button>

          {showAdvanced && (
            <>
              <div className="form-group">
                <DSInput
                  label="Featured Image URL"
                  type="url"
                  name="featured_image_url"
                  value={formData.event_metadata.featured_image_url}
                  onChange={handleMetadataChange}
                  placeholder="https://example.com/image.jpg"
                />
                <small>Used for WordPress and promotional materials</small>
              </div>

              <div className="form-group">
                <DSTextarea
                  label="Rich HTML Description (Optional)"
                  name="html_description"
                  value={formData.html_description}
                  onChange={handleInputChange}
                  rows="5"
                  placeholder="<p>Advanced HTML formatting...</p>"
                />
                <small>Full HTML description for WordPress</small>
              </div>
            </>
          )}
        </div>
        )}

        {/* Copy/Paste Helpers */}
        {pinballSubtype === 'tournament' && (
        <div className="form-section">
          <h3><DSIcon name="clipboard" /> IFPA Overall Details</h3>
          
          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label>Copy/Paste for IFPA Registration</label>
              <DSButton
                type="button"
                appearance="outline"
                tone="secondary"
                size="sm"
                onClick={() => copyToClipboard(generateIFPADetails())}
              >
                <DSIcon name="clipboard" /> Copy
              </DSButton>
            </div>
            <DSTextarea
              readOnly
              value={generateIFPADetails()}
              rows="15"
              style={{ fontFamily: 'monospace', fontSize: '14px', backgroundColor: 'var(--ds-color-surfaceMuted)' }}
            />
            <small>Copy this complete text for the IFPA "Overall Details" field when registering your tournament. It includes all the information needed for IFPA and can also be used as a reference for MatchPlay setup.</small>
          </div>
        </div>
        )}

        {/* Form Actions */}
        <div className="form-actions">
          {pinballSubtype === 'pingolf' ? (
            <>
              <DSButton
                appearance="outline"
                tone="neutral"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </DSButton>
              <DSButton
                type="submit"
                appearance="solid"
                tone="primary"
                disabled={submitting}
                leadingIcon={<DSIcon name="plus" size="sm" tone="default" />}
              >
                {submitting ? 'Creating...' : 'Create PinGolf Event'}
              </DSButton>
            </>
          ) : !createdEventId ? (
            <>
              <DSButton
                type="button"
                appearance="outline"
                tone="neutral"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </DSButton>
              <DSButton
                type="submit"
                appearance="solid"
                tone="primary"
                disabled={submitting}
              >
                {submitting ? 'Creating...' : 'Create Tournament Event'}
              </DSButton>
            </>
          ) : (
            <>
              <DSButton
                type="button"
                appearance="outline"
                tone="neutral"
                onClick={onClose}
              >
                Close
              </DSButton>
              <DSButton
                type="button"
                appearance="outline"
                tone="secondary"
                onClick={handleSyncToWordPress}
                disabled={syncing || formData.wordpress_url}
              >
                {syncing ? 'Syncing...' : formData.wordpress_url ? <><DSIcon name="check" /> Synced to WordPress</> : 'Sync to WordPress'}
              </DSButton>
              <DSButton
                type="button"
                appearance="solid"
                tone="primary"
                onClick={handleUpdateEvent}
                disabled={submitting || !formData.event_metadata.matchplay_url}
              >
                {submitting ? 'Updating...' : 'Update & Re-sync'}
              </DSButton>
            </>
          )}
        </div>
      </form>
    </div>
  );
}

export default PinballEventForm;
